import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from './confirmdialog.component';
import { InfoDialogComponent } from './infodialog.component';
import { ProgressDialogComponent } from './progressdialog.component';

import { tryCatch } from '@orgbrain/lib-util';
import { firstValueFrom } from 'rxjs';
import { BlockingDialogComponent } from './blockingdialog.component';
import { CompletionDialogComponent } from './completion-dialog/completion-dialog.component';
import { DeleteDialogComponent } from './deletedialog.component';
import { ErrorDialogComponent } from './errordialog/errordialog.component';
import { InfoOptions, InfoParams, SonnerOptions } from './info';
import { MultiChoiceDialogComponent } from './multichoicedialog.component';
import { WorkingDialogComponent } from './workingdialog.component';
import { toast } from 'ngx-sonner';
import { SonnerComponent } from './sonner.component';
import { OptionalTranslationHelper } from '@orgbrain/lib-data';

type DefaultDialogOptions = {
  titleKey: string;
  questionKey: string;
  yesOptionTitleKey: string;
  noOptionTitleKey: string;
};
type ConfirmDialogOptions = DefaultDialogOptions & {
  alternativeOptionTitle: string;
  reverseLogic: boolean;
  disableClose: false;
};

const defaultSuccess: SonnerOptions = {
  titleKey: 'Success',
  status: 'success',
};

@Injectable({ providedIn: 'root' })
export class OrgBrainDialogService {
  private dialog: MatDialog = inject(MatDialog);
  private locale: string = inject(LOCALE_ID);

  translations = new OptionalTranslationHelper(null, this.locale);

  openWorkingDialog(title: string, body?: string) {
    const progressDialog = this.dialog.open(WorkingDialogComponent, {
      autoFocus: false,
      backdropClass: this.defaultBackdrop(),
      width: '500px',
      disableClose: true,
      data: { title, body },
    });
    return progressDialog;
  }

  openProgressDialog() {
    ProgressDialogComponent.open(this.dialog);
  }

  closeProgressDialog() {
    ProgressDialogComponent.close();
  }

  openDeleteDialog(
    title: string = this.translations.translate('Warning'),
    question: string | string[] = this.translations.translate('DeleteGenericObjectText'),
    yesOptionTitle: string = this.translations.translate('Delete'),
    noOptionTitle: string = this.translations.translate('Cancel')
  ): MatDialogRef<DeleteDialogComponent> {
    const deleteDialog = this.dialog.open(DeleteDialogComponent, {
      autoFocus: false,
      backdropClass: 'darkerBackdrop',
      width: '470px',
      disableClose: true,
    });

    deleteDialog.componentInstance.title = this.translations.translate(title);
    deleteDialog.componentInstance.question = this.translations.translate(question);
    deleteDialog.componentInstance.yesOptionTitle = this.translations.translate(yesOptionTitle);
    deleteDialog.componentInstance.noOptionTitle = this.translations.translate(noOptionTitle);
    return deleteDialog;
  }

  async deleteDialogPromise(
    props: Partial<DefaultDialogOptions> & { onAccepted?: () => Promise<unknown> }
  ) {
    let { titleKey, questionKey, yesOptionTitleKey, noOptionTitleKey, onAccepted } = props;
    const title = this.translations.translate(titleKey ?? 'Warning');
    const question = this.translations.translate(questionKey ?? 'DeleteGenericObjectText');
    const yesOptionTitle = this.translations.translate(yesOptionTitleKey ?? 'Delete');
    const noOptionTitle = this.translations.translate(noOptionTitleKey ?? 'Cancel');

    const deleteDialog = this.openDeleteDialog(title, question, yesOptionTitle, noOptionTitle);
    const isAccepted = await firstValueFrom(deleteDialog.afterClosed());
    if (!onAccepted || !isAccepted) return isAccepted;

    const result = await tryCatch(onAccepted());
    if (result.ok === false) {
      await this.errorDialogPromise({ error: result.error.message });
      return false;
    }

    return isAccepted;
  }

  openCompletionDialog(
    title: string = 'CofirmationDialogGenericTitle',
    message: string | string[] = 'CofirmationDialogGenericMessage',
    okOptionTitle: string = 'OK'
  ): MatDialogRef<CompletionDialogComponent> {
    const completionDialog = this.dialog.open(CompletionDialogComponent, {
      autoFocus: false,
      backdropClass: 'darkerBackdrop',
      width: '470px',
    });

    completionDialog.componentInstance.title = this.translations.translate(title);
    completionDialog.componentInstance.message = this.translations.translate(message);
    completionDialog.componentInstance.okOptionTitle = this.translations.translate(okOptionTitle);
    return completionDialog;
  }

  async completionDialogPromise(
    props: Partial<{
      titleKey: string;
      messageKey: string;
      okOptionTitleKey: string;
      onAccepted?: () => Promise<unknown>;
    }>
  ) {
    let { titleKey, messageKey, okOptionTitleKey, onAccepted } = props;
    const title = this.translations.translate(titleKey ?? 'Success');
    const message = this.translations.translate(messageKey ?? 'OperationCompletedSuccessfully');
    const okOptionTitle = this.translations.translate(okOptionTitleKey ?? 'OK');

    const completionDialog = this.openCompletionDialog(title, message, okOptionTitle);
    const isAccepted = await firstValueFrom(completionDialog.afterClosed());
    if (!onAccepted || !isAccepted) return isAccepted;

    const res = await tryCatch(onAccepted());
    if (res.ok === true) return isAccepted;

    await this.errorDialogPromise({ error: res.error.message });
    return false;
  }

  openConfirmDialog(
    title: string = this.translations.translate('AreYouSure'),
    question: string | string[] = this.translations.translate('Pleaseconfirmtoproceed'),
    yesOptionTitle: string = 'OK',
    noOptionTitle: string = this.translations.translate('Cancel'),
    alternativeOptionTitle: string = null,
    reverseLogic: boolean = false,
    disableClose: boolean = true
  ): MatDialogRef<ConfirmDialogComponent> {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      backdropClass: 'darkerBackdrop',
      width: '470px',
      disableClose: disableClose,
      data: { reverseLogic },
    });

    confirmDialog.componentInstance.title = this.translations.translate(title);
    confirmDialog.componentInstance.question = this.translations.translate(question);
    confirmDialog.componentInstance.yesOptionTitle = this.translations.translate(yesOptionTitle);
    confirmDialog.componentInstance.noOptionTitle = this.translations.translate(noOptionTitle);
    confirmDialog.componentInstance.alternativeOptionTitle =
      alternativeOptionTitle && this.translations.translate(alternativeOptionTitle);

    return confirmDialog;
  }

  async confirmDialogPromise(
    props: Partial<
      Pick<
        ConfirmDialogOptions,
        'titleKey' | 'questionKey' | 'yesOptionTitleKey' | 'noOptionTitleKey' | 'disableClose'
      >
    >
  ) {
    let { titleKey, questionKey, yesOptionTitleKey, noOptionTitleKey, disableClose } = props;
    const title = this.translations.translate(titleKey ?? 'AreYouSure');
    const question = this.translations.translate(questionKey ?? 'ConfirmDialogDefaultText');
    const yesOptionTitle = this.translations.translate(yesOptionTitleKey ?? 'Continue');
    const noOptionTitle = this.translations.translate(noOptionTitleKey ?? 'Cancel');

    const confirmDialog = this.openConfirmDialog(
      title,
      question,
      yesOptionTitle,
      noOptionTitle,
      null,
      false,
      disableClose
    );
    return (await firstValueFrom(confirmDialog.afterClosed())) as boolean | null;
  }

  openInfoDialog(title: string, infotext: string, overrideparams?: any) {
    const params = {
      data: new InfoParams({ title, message: infotext }),
      autoFocus: false,
      backdropClass: this.defaultBackdrop(),
      width: '470px',
    };

    Object.assign(params, overrideparams);

    return this.dialog.open(InfoDialogComponent, params);
  }

  openMultiDialog({
    title,
    infotext,
    options,
    imageUrl,
    noCancel,
  }: {
    title: string;
    infotext: string;
    options: InfoOptions[];
    imageUrl?: string;
    noCancel?: boolean;
  }): MatDialogRef<MultiChoiceDialogComponent> {
    return this.dialog.open(MultiChoiceDialogComponent, {
      data: new InfoParams({ title, message: infotext, options, imageUrl, noCancel }),
      autoFocus: false,
      backdropClass: this.defaultBackdrop(),
      disableClose: noCancel,
      width: '470px',
    });
  }

  openErrorDialog({
    title,
    error,
    message,
  }: {
    title: string;
    error: any;
    message?: string;
  }): MatDialogRef<ErrorDialogComponent> {
    window['hiddonErrorList'] = window['hiddonErrorList'] || [];
    window['hiddonErrorList'].push(error);
    return this.dialog.open(ErrorDialogComponent, {
      data: new InfoParams({ title, error, message }),
      autoFocus: false,
      backdropClass: this.defaultBackdrop(),
      width: '470px',
    });
  }

  /**
   * Triggers the toast notification based on the params provided.
   */
  public show(options: SonnerOptions): void {
    const data: SonnerOptions = {
      titleKey: this.translations.translate(options.titleKey),
      messageKey: this.translations.translate(options.messageKey),
      error: options.error,
      status: options.status,
      requestId: options.requestId,
    };
    const id = toast.custom(SonnerComponent, {
      componentProps: { data },
      description: options.messageKey,
      duration: 5000,
    });

    // This is how we ensure that the toast appears after the current call stack is cleared.
    setTimeout(() => {
      toast.custom(SonnerComponent, {
        id,
        componentProps: { data, id },
        description: options.messageKey,
        duration: 5000,
      });
    });
  }

  /**
   * Displays an error toast notification.
   *
   * @param props - Configuration options for the error notification.
   *
   * SECURITY WARNING: The `error` field in props should be used with extreme caution.
   * Displaying raw error messages to users can expose sensitive information such as:
   * - Database schema details
   * - File paths and server structure
   * - API implementation details
   * - Internal system information
   *
   * RECOMMENDED APPROACH:
   * - Use `titleKey` and `messageKey` for user-friendly, localized error messages
   * - Include `requestId` for error tracking (safe to display to users)
   * - Avoid passing `error` unless you've explicitly sanitized the message
   *
   * @example
   * // ✅ RECOMMENDED: Use translation keys and requestId
   * const result = await tryCatch(this.trpc.api().someRouter.someMethod.mutate(data));
   * if (!result.ok) {
   *   return this.orgbrainDialog.errorDialogPromise({
   *     titleKey: 'ErrorTitle',
   *     messageKey: 'ErrorMessage',
   *     requestId: result.error?.data?.requestId
   *   });
   * }
   *
   * // ⚠️ USE WITH CAUTION: Only if error message is sanitized
   * this.orgbrainDialog.errorDialogPromise({
   *   titleKey: 'ErrorTitle',
   *   messageKey: 'ErrorMessage',
   *   error: sanitizedErrorMessage, // Make sure this is safe!
   *   requestId: requestId
   * });
   */
  async errorDialogPromise(props?: Partial<SonnerOptions>) {
    const options: SonnerOptions = {
      titleKey: props?.titleKey || 'InternalServerErrorTitle',
      messageKey: props?.messageKey || 'InternalServerErrorMessage',
      requestId: props?.requestId,
      error: props?.error,
      status: 'error',
    };

    this.show(options);
  }

  /**
   * Displays an informational toast notification.
   * Useful for providing non-critical updates or information.
   *
   * @param props - Configuration options including title and message key.
   */
  async infoDialogPromise(props: { titleKey: string; messageKey: string }) {
    const { titleKey, messageKey } = props;
    this.show({ titleKey, messageKey, status: 'info' });
  }

  /**
   * Displays a success toast notification.
   * Useful for confirming successful actions.
   *
   * @param props - Configuration options including title and message key.
   */
  async successDialogPromise(props: { titleKey: string; messageKey: string }) {
    const { titleKey, messageKey } = { ...defaultSuccess, ...props };
    this.show({ titleKey, messageKey, status: 'success' });
  }

  openBlockingDialog({
    title,
    infotext,
    dismiss,
    progress,
  }: {
    title: string;
    infotext: string;
    dismiss?: string;
    progress?: {
      loaded: number;
      total: number;
      percentage?: number;
      showProgressBar?: boolean;
      hideTextProgress?: boolean;
    };
  }) {
    return this.dialog.open(BlockingDialogComponent, {
      data: new InfoParams({ title, message: infotext, progress, dismiss }),
      autoFocus: false,
      backdropClass: this.defaultBackdrop(),
      width: '500px',
      disableClose: true,
    });
  }

  showOnlyTop() {
    const all: MatDialogRef<any>[] = this.dialog.openDialogs;
    const top = all[all.length - 1];

    for (const d of all) {
      const container = d._containerInstance as any;

      const el = container?._elementRef.nativeElement;
      if (el) {
        if (d !== top) {
          el.style.display = 'none';
        } else {
          el.style.display = 'block';
          d.afterClosed().subscribe(() => {
            this.showOnlyTop();
          });
        }
      }
    }
  }

  defaultBackdrop(): string {
    if (this.dialog.openDialogs.length === 0) {
      return 'darkerBackdrop';
    } else {
      return 'clearBackdrop';
    }
  }
}
